import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'

import { LocaleKeys } from '../../../../locale/translation-types'
import { getTranslatedName } from '../../../utils/functions/translation.functions'
import { AccomodationEnum } from '../../enums/accomodation-type/accomodation-type'
import { GenderEnum } from '../../enums/common/gender.enum'
import {
  ApplicationStatusEnum,
  applicationStatusEnumMapping
} from '../../enums/enrollment/application-status.enum'
import { ContactEnum } from '../../enums/enrollment/contact.enum'
import { EnrollmentPropositionTypeEnum } from '../../enums/enrollment/enrollment-proposition-type.enum'
import { medicalConditionEnumMapping } from '../../enums/enrollment/medical-condition-enum'
import { enrollmentScoreEnumMapping } from '../../enums/enrollment/score.enum'
import { InsuranceTypeEnum } from '../../enums/insurance/insurance-type.enum'
import { AccomodationCategoryEnum } from '../../enums/product/accomodation-category.enum'
import { ProductTypeEnum } from '../../enums/product/product-type.enum'
import { EnrollmentBaseType } from '../../fields/enrollments/enrollment'
import { Pair, StatusOption } from '../../utils'
import { FriendInput } from '../friend/friend-input'
import { HostFamilyLight } from '../host-family/host-family'
import { InsuranceProperties } from '../insurance/properties.insurance'
import { Journey } from '../product/journey'
import { PLANE_TRANSPORT_MEANS_CODE } from '../transport-means/transport-means'
import { TripLabelInput, TripProperties } from '../trip/properties.trip'
import { Enrollment } from './enrollment'
import { EnrollmentHealth } from './enrollment-health'

export type ParentInformationType = {
  parentGender?: GenderEnum
  parentFirstName?: string
  parentLastName?: string
  parentProfession?: string
  parentPhoneNumber?: string
  parentPhoneNumber2?: string
  parentEmail?: string
}

// ─── per-method input types ──────────────────────────────────────────────────

type GetMedicalStateInput = Pick<Enrollment, 'isValidMedicalStatus' | 'medicalLevel'>

type MainPropositionType = NonNullable<Enrollment['mainProposition']>

type OtoIndividualPropositionType = NonNullable<Enrollment['otoIndividualProposition']>

type GetEnrollmentCountryInput = {
  propositionType?: Enrollment['propositionType']
  mainProposition?: {
    journey?: {
      date: {
        product: {
          country: Pick<
            MainPropositionType['journey']['date']['product']['country'],
            'code' | 'name'
          >
        }
      }
    }
  }
  otoIndividualProposition?: {
    product: { country: Pick<OtoIndividualPropositionType['product']['country'], 'code' | 'name'> }
  }
}

type GetEnrollmentFormulaInput = {
  propositionType?: Enrollment['propositionType']
  mainProposition?: {
    journey?: {
      date: {
        product: {
          formula: Pick<
            MainPropositionType['journey']['date']['product']['formula'],
            'id' | 'translations'
          >
        }
      }
    }
  }
  otoIndividualProposition?: {
    product: {
      formula?: Pick<OtoIndividualPropositionType['product']['formula'], 'id' | 'translations'>
    }
  }
}

type GetEnrollmentMainOptionInput = {
  propositionType?: Enrollment['propositionType']
  mainProposition?: {
    journey?: { mainOption: Pick<MainPropositionType['journey']['mainOption'], 'translations'> }
  }
  otoIndividualProposition?: {
    option?: Pick<NonNullable<OtoIndividualPropositionType['option']>, 'translations'>
  }
}

type GetEnrollmentHoursPerWeekInput = {
  propositionType?: Enrollment['propositionType']
  mainProposition?: {
    journey?: {
      date: {
        product: Pick<MainPropositionType['journey']['date']['product'], 'numberOfHoursPerWeek'>
      }
    }
  }
  otoIndividualProposition?: Pick<OtoIndividualPropositionType, 'numberOfHours'>
}

type GetEnrollmentOtoHoursPerWeekInput = GetEnrollmentHoursPerWeekInput & {
  mainProposition?: {
    journey?: {
      date: {
        product: Pick<MainPropositionType['journey']['date']['product'], 'type'>
      }
    }
  }
}

type GetEnrollmentOptionsInput = {
  mainProposition?: {
    options?: {
      option?: Pick<NonNullable<MainPropositionType['options'][number]['option']>, 'translations'>
    }[]
  }
}

export type EnrollmentDatesInput = {
  propositionType?: Enrollment['propositionType']
  mainProposition?: {
    journey?: {
      date: Pick<MainPropositionType['journey']['date'], 'departureDate' | 'returnDate'> & {
        period?: Pick<NonNullable<MainPropositionType['journey']['date']['period']>, 'color'>
      }
    }
  }
  otoIndividualProposition?: Pick<OtoIndividualPropositionType, 'departureDate' | 'returnDate'> & {
    period?: Pick<NonNullable<OtoIndividualPropositionType['period']>, 'color'>
  }
}

export type EnrollmentAgeAtDepartureInput = {
  childBirthDate?: string | Date | null
  propositionType?: Enrollment['propositionType']
  mainProposition?: {
    journey?: {
      date?: Pick<MainPropositionType['journey']['date'], 'departureDate'> | null
    } | null
  } | null
  otoIndividualProposition?: Pick<OtoIndividualPropositionType, 'departureDate'> | null
}

type GetIsRepatriationInsuranceInput = {
  mainProposition?: {
    insurances?: {
      insurance: Pick<MainPropositionType['insurances'][number]['insurance'], 'type'>
    }[]
  }
}

type GetEnrollmentVaccinationInput = {
  enrollmentHealth?: Pick<
    EnrollmentHealth,
    | 'diphtheria'
    | 'tetanus'
    | 'poliomyelitis'
    | 'dtPolioTetracoq'
    | 'pertussis'
    | 'hepatitisB'
    | 'rubellaMumpsMeasles'
    | 'bcg'
    | 'haemophilus'
    | 'pneumococcus'
    | 'meningococcusC'
  >
}

type GetEnrollmentTripPlaceInput = {
  mainProposition?: {
    journeyTripPlace?: Pick<NonNullable<MainPropositionType['journeyTripPlace']>, 'translations'>
  }
}

type GetEnrollmentTransportInput = {
  propositionType?: Enrollment['propositionType']
  mainProposition?: {
    mainTrip?: {
      transportMeans?: Pick<
        NonNullable<NonNullable<MainPropositionType['mainTrip']>['transportMeans']>,
        'translations' | 'code'
      >
      departure?: Pick<
        NonNullable<NonNullable<MainPropositionType['mainTrip']>['departure']>,
        'translations'
      >
    }
  }
}

type GetEnrollmentTitleWithoutAAInput = Pick<
  Enrollment,
  'childFirstName' | 'childLastName' | 'enrollmentNumber'
>

type GetEnrollmentTitleInput = GetEnrollmentTitleWithoutAAInput & Pick<Enrollment, 'isAA'>

type GetEnrollmentStatusBadgeInput = Pick<Enrollment, 'medicalStatus' | 'status'>

type GetEnrollmentNameInput = Pick<Enrollment, 'childFirstName' | 'childLastName'>

/** Composed from the types of the four methods it delegates to */
type GetEnrollmentLabelInput = EnrollmentDatesInput &
  GetEnrollmentFormulaInput &
  GetEnrollmentMainOptionInput &
  GetEnrollmentOptionsInput

type GetParentInformationInput = Pick<
  Enrollment,
  | 'parent1Gender'
  | 'parent1FirstName'
  | 'parent1LastName'
  | 'parent1Job'
  | 'parent1PhoneNumber'
  | 'parent1PhoneNumber2'
  | 'parent1Email'
  | 'parent2Gender'
  | 'parent2FirstName'
  | 'parent2LastName'
  | 'parent2Job'
  | 'parent2PhoneNumber'
  | 'parent2PhoneNumber2'
  | 'parent2Email'
>

type GetParentsByPriorityInput = Pick<Enrollment, 'contactPriority'>

type GetEnrollmentScoreInput = {
  languageTestSubmission?: Pick<
    NonNullable<Enrollment['languageTestSubmission']>,
    'score' | 'level'
  >
}

/** Composed from the types of the four methods it delegates to */
export type EnrollmentDetailsInput = GetEnrollmentCountryInput &
  GetEnrollmentFormulaInput &
  GetEnrollmentMainOptionInput &
  GetEnrollmentOptionsInput

/** Composed from delegate method types + journey.reference */
type GetEnrollmentJourneyDetailsInput = GetEnrollmentFormulaInput &
  GetEnrollmentMainOptionInput &
  GetEnrollmentOptionsInput &
  EnrollmentDatesInput & {
    mainProposition?: { journey?: Pick<Journey, 'reference'> }
  }

// a One to One Individuel enrollment has no trip at all, so the proposition may be absent
export type EnrollmentMainTripDetailsInput = {
  mainProposition?: { mainTrip?: TripLabelInput }
}

export type EnrollmentSecondaryTripDetailsInput = {
  mainProposition?: { secondaryTrip?: TripLabelInput }
}

type GetEnrollmentAccomodationInput = {
  enrollmentCenter?: {
    center: Pick<NonNullable<Enrollment['enrollmentCenter']>['center'], 'accomodation'>
  }
}

type HasCompatibleCenterAccomodationInput = {
  mainProposition?: {
    journey?: {
      date: { product: { accomodationCategory: AccomodationCategoryEnum } }
    }
  }
}

type CenterAccomodationInput = { accomodation: AccomodationEnum }

type GetHostFamilyInput = {
  enrollmentCenter?: { hostFamily?: HostFamilyLight }
}

export class EnrollmentProperties {
  static isOtoIndividual(enrollment?: {
    propositionType?: Enrollment['propositionType']
  }): boolean {
    return enrollment?.propositionType === EnrollmentPropositionTypeEnum.OTO_INDIVIDUAL
  }

  static getMedicalState(enrollment: GetMedicalStateInput): StatusOption {
    if (enrollment.isValidMedicalStatus) return { label: 'Validé', variant: 'success' }
    return medicalConditionEnumMapping[enrollment.medicalLevel]
  }

  static getEnrollmentCountry(enrollment?: GetEnrollmentCountryInput): Pair<string | undefined> {
    return {
      key: 'mainProposition.journey.date.product.country.name',
      value: this.isOtoIndividual(enrollment)
        ? enrollment?.otoIndividualProposition?.product.country.code
        : enrollment?.mainProposition?.journey?.date.product.country.code
    }
  }

  static getEnrollmentCountryName(
    enrollment?: GetEnrollmentCountryInput
  ): Pair<string | undefined> {
    return {
      key: 'mainProposition.journey.date.product.country.name',
      value: this.isOtoIndividual(enrollment)
        ? enrollment?.otoIndividualProposition?.product.country.name
        : enrollment?.mainProposition?.journey?.date.product.country.name
    }
  }

  static getEnrollmentFormula(enrollment?: GetEnrollmentFormulaInput): Pair<string | undefined> {
    return {
      key: 'mainProposition.journey.date.product.formula.title',
      value: this.isOtoIndividual(enrollment)
        ? getTranslatedName(enrollment?.otoIndividualProposition?.product.formula) || undefined
        : getTranslatedName(enrollment?.mainProposition?.journey?.date.product.formula) ||
          undefined
    }
  }

  static getEnrollmentMainOption(
    enrollment?: GetEnrollmentMainOptionInput
  ): Pair<string | undefined> {
    return {
      key: 'mainProposition.journey.mainOption.title',
      value: this.isOtoIndividual(enrollment)
        ? getTranslatedName(enrollment?.otoIndividualProposition?.option) || undefined
        : getTranslatedName(enrollment?.mainProposition?.journey?.mainOption) || undefined
    }
  }

  // the weekly tuition volume: a One to One Individuel stay carries it on its own proposition,
  // every other stay reads it from the journey product
  static getEnrollmentHoursPerWeek(
    enrollment?: GetEnrollmentHoursPerWeekInput
  ): Pair<number | undefined> {
    return {
      key: 'mainProposition.journey.date.product.numberOfHoursPerWeek',
      // the journey product's volume is nullable in the database and reaches us as null
      value:
        (this.isOtoIndividual(enrollment)
          ? enrollment?.otoIndividualProposition?.numberOfHours
          : enrollment?.mainProposition?.journey?.date.product.numberOfHoursPerWeek) ?? undefined
    }
  }

  // the weekly tuition volume only for One to One stays (accompanied or individual):
  // the other formulas have no tuition hours to show
  static getEnrollmentOtoHoursPerWeek(
    enrollment?: GetEnrollmentOtoHoursPerWeekInput
  ): Pair<number | undefined> {
    const hoursPerWeek = this.getEnrollmentHoursPerWeek(enrollment)
    const isOto =
      this.isOtoIndividual(enrollment) ||
      enrollment?.mainProposition?.journey?.date.product.type === ProductTypeEnum.ONE_TO_ONE
    return { ...hoursPerWeek, value: isOto ? hoursPerWeek.value : undefined }
  }

  static getEnrollmentOptions(enrollment?: GetEnrollmentOptionsInput): Pair<string | undefined> {
    const options = enrollment?.mainProposition?.options?.map((option) =>
      getTranslatedName(option.option)
    )
    return {
      key: 'mainProposition.options.option.title',
      value: options?.length ? options.join(', ') : undefined
    }
  }

  static getEnrollmentDepartureAndArrivalDates(
    enrollment?: EnrollmentDatesInput
  ): Pair<{ startDate: string; endDate: string; color: string } | undefined> {
    const source = this.isOtoIndividual(enrollment)
      ? enrollment?.otoIndividualProposition
      : enrollment?.mainProposition?.journey?.date
    const startDate = source?.departureDate
    const endDate = source?.returnDate
    if (!startDate || !endDate)
      return {
        key: 'mainProposition.journey.date.departureDate',
        value: undefined
      }

    return {
      key: 'mainProposition.journey.date.departureDate',
      value: { startDate, endDate, color: source?.period?.color ?? '#000000' }
    }
  }

  /**
   * Age of the enrollee on the stay departure date (same source as the backend departure date).
   * Null when the birth date or the departure date is missing, never falls back to today.
   */
  static getAgeAtDeparture(enrollment?: EnrollmentAgeAtDepartureInput): number | null {
    const departureDate = this.isOtoIndividual(enrollment)
      ? enrollment?.otoIndividualProposition?.departureDate
      : enrollment?.mainProposition?.journey?.date?.departureDate
    if (!enrollment?.childBirthDate || !departureDate) return null

    return DateFormatter.calculateRelativeAge(enrollment.childBirthDate, departureDate)
  }

  static isRepatriationInsurance(enrollment?: GetIsRepatriationInsuranceInput): boolean {
    if (!enrollment) return false

    return InsuranceProperties.hasEnrollmentInsuranceType(
      enrollment.mainProposition?.insurances,
      InsuranceTypeEnum.REPATRIATION
    )
  }

  static getEnrollmentVaccination(
    enrollment?: GetEnrollmentVaccinationInput
  ): Pair<Date>[] | undefined {
    const vaccinations: Pair<Date>[] = []

    if (!enrollment || !enrollment.enrollmentHealth) return undefined
    const { enrollmentHealth } = enrollment

    const vaccinationMap = {
      diphtheria: 'enrollment.vaccinations.diphtheria',
      tetanus: 'enrollment.vaccinations.tetanus',
      poliomyelitis: 'enrollment.vaccinations.poliomyelitis',
      dtPolioTetracoq: 'enrollment.vaccinations.dtPolioTetracoq',
      pertussis: 'enrollment.vaccinations.pertussis',
      hepatitisB: 'enrollment.vaccinations.hepatitisB',
      rubellaMumpsMeasles: 'enrollment.vaccinations.rubellaMumpsMeasles',
      bcg: 'enrollment.vaccinations.bcg',
      haemophilus: 'enrollment.vaccinations.haemophilus',
      pneumococcus: 'enrollment.vaccinations.pneumococcus',
      meningococcusC: 'enrollment.vaccinations.meningococcusC'
    } as Record<keyof typeof enrollmentHealth, LocaleKeys>

    Object.keys(vaccinationMap).forEach((key) => {
      const item = enrollmentHealth[key as keyof typeof enrollmentHealth]
      if (item && typeof item === 'string')
        vaccinations.push({
          key: vaccinationMap[key as keyof typeof vaccinationMap],
          value: DateFormatter.toDate(item)
        })
    })
    return vaccinations
  }

  static getEnrollmentDates(
    enrollment?: EnrollmentDatesInput
  ): Pair<{ startDate: string; endDate: string; color: string } | undefined> {
    const dates = this.getEnrollmentDepartureAndArrivalDates(enrollment).value
    if (!dates) return { key: 'mainProposition.journey.date.departureDate', value: undefined }
    const startDate = DateFormatter.DDMMYYYY(dates.startDate)
    const endDate = DateFormatter.DDMMYYYY(dates.endDate)

    return {
      key: 'mainProposition.journey.date.departureDate',
      value: { startDate, endDate, color: dates.color ?? '#000000' }
    }
  }

  static getEnrollmentTripPlace(
    enrollment?: GetEnrollmentTripPlaceInput
  ): Pair<string | undefined> {
    return {
      key: 'mainProposition.journeyTripPlace.label',
      value: getTranslatedName(enrollment?.mainProposition?.journeyTripPlace) || undefined
    }
  }

  static getEnrollmentTransport(
    enrollment?: GetEnrollmentTransportInput,
    options?: { withPlaneDepartureCity?: boolean }
  ): Pair<string | undefined> {
    const key = 'mainProposition.mainTrip.transportMeans.name'
    // an OTO Individual enrollment never carries a trip
    if (this.isOtoIndividual(enrollment))
      return { key, value: TripProperties.handleTransportMeansName(undefined) }

    const mainTrip = enrollment?.mainProposition?.mainTrip
    const transportName = TripProperties.handleTransportMeansName(mainTrip?.transportMeans)
    const isPlane = mainTrip?.transportMeans?.code === PLANE_TRANSPORT_MEANS_CODE
    const departureCity =
      options?.withPlaneDepartureCity && isPlane
        ? getTranslatedName(mainTrip?.departure)
        : undefined

    return { key, value: [departureCity, transportName].filter(Boolean).join(' - ') }
  }

  static getEnrollmentTitleWithoutAA(enrollment: GetEnrollmentTitleWithoutAAInput): string {
    const { childFirstName, childLastName, enrollmentNumber } = enrollment
    const result = []
    if (childFirstName || childLastName) result.push(toFullName(childFirstName, childLastName))
    if (enrollmentNumber) result.push(enrollmentNumber)
    return result.join(' - ')
  }

  static getEnrollmentTitle(enrollment: GetEnrollmentTitleInput): string {
    const title = EnrollmentProperties.getEnrollmentTitleWithoutAA(enrollment)
    if (!enrollment.isAA) return title
    return `${title} - AA`
  }

  static getEnrollmentStatusBadge(enrollment: GetEnrollmentStatusBadgeInput): StatusOption {
    return applicationStatusEnumMapping[enrollment.status]
  }

  static getEnrollmentName(enrollment: GetEnrollmentNameInput): string {
    return toFullName(enrollment.childFirstName, enrollment.childLastName, {
      options: { mode: 'lastname-firstname' }
    })
  }

  // 01/06/2025 - 15/06/2025 - Standard - Thème - Option
  static getEnrollmentLabel(enrollment: GetEnrollmentLabelInput): string {
    const result: string[] = []

    const dates = EnrollmentProperties.getEnrollmentDates(enrollment).value
    const formula = EnrollmentProperties.getEnrollmentFormula(enrollment).value
    const mainOption = EnrollmentProperties.getEnrollmentMainOption(enrollment).value
    const options = EnrollmentProperties.getEnrollmentOptions(enrollment).value

    if (dates) {
      result.push(dates.startDate)
      result.push(dates.endDate)
    }

    if (formula) result.push(formula)
    if (mainOption) result.push(mainOption)
    if (options) result.push(options)

    return result.join(' - ')
  }

  static getParentInformation(
    enrollment: GetParentInformationInput,
    parent: '1' | '2'
  ): ParentInformationType {
    const parentGender = enrollment[`parent${parent}Gender`]
    const parentFirstName = enrollment[`parent${parent}FirstName`]
    const parentLastName = enrollment[`parent${parent}LastName`]
    const parentProfession = enrollment[`parent${parent}Job`]
    const parentPhoneNumber = enrollment[`parent${parent}PhoneNumber`]
    const parentPhoneNumber2 = enrollment[`parent${parent}PhoneNumber2`]
    const parentEmail = enrollment[`parent${parent}Email`]

    return {
      parentGender,
      parentFirstName,
      parentLastName,
      parentProfession,
      parentPhoneNumber,
      parentPhoneNumber2,
      parentEmail
    }
  }

  static getParentsByPriority(enrollment: GetParentsByPriorityInput): {
    parent1: '1' | '2'
    parent2: '1' | '2'
  } {
    const parent1 = enrollment.contactPriority === ContactEnum.PARENT_1 ? '1' : '2'
    const parent2 = parent1 === '1' ? '2' : '1'
    return { parent1, parent2 }
  }

  /**
   * group enrollments by child
   * @param enrollments
   * @returns {Record<string, EnrollmentLight[]>} - key is child name, value is array of enrollments
   *
   */
  static groupEnrollmentsByChild<T extends Pick<Enrollment, 'childFirstName' | 'childLastName'>>(
    enrollments: T[]
  ): Record<string, T[]> {
    return enrollments.reduce(
      (acc, enrollment) => {
        const childName = toFullName(enrollment.childFirstName, enrollment.childLastName)
        if (!acc[childName]) {
          acc[childName] = []
        }
        acc[childName].push(enrollment)
        return acc
      },
      {} as Record<string, T[]>
    )
  }

  static getEnrollmentScore(enrollment: GetEnrollmentScoreInput) {
    if (!enrollment.languageTestSubmission) return undefined
    const { score, level } = enrollment.languageTestSubmission
    if (!score || !level) return undefined
    const { code, label } = enrollmentScoreEnumMapping[level]
    return { label, score, code }
  }

  /**
   * Get enrollment details including country, formula, theme, and options
   * @param enrollment
   * @returns strings representing the details of the enrollment
   */
  static getEnrollmentDetails(enrollment: EnrollmentDetailsInput): string {
    const country = this.getEnrollmentCountryName(enrollment).value
    const formula = this.getEnrollmentFormula(enrollment).value
    const theme = this.getEnrollmentMainOption(enrollment).value
    const options = this.getEnrollmentOptions(enrollment).value

    // a One to One Individuel enrollment may have no option, so only the present parts are joined
    return [country, formula, theme, options].filter(Boolean).join(' - ')
  }

  /**
   * Get enrollment journey details
   * @param enrollment
   * @returns strings representing the details of the enrollment
   */
  static getEnrollmenJourneytDetails(enrollment: GetEnrollmentJourneyDetailsInput): string {
    const reference = enrollment.mainProposition?.journey?.reference
    const formula = this.getEnrollmentFormula(enrollment).value
    const theme = this.getEnrollmentMainOption(enrollment).value
    const options = this.getEnrollmentOptions(enrollment).value
    const dates = this.getEnrollmentDepartureAndArrivalDates(enrollment).value
    const startDate = dates ? DateFormatter.DDMMYYYY(dates.startDate) : ''
    const endDate = dates ? DateFormatter.DDMMYYYY(dates.endDate) : ''

    return `${reference ? `${reference} - ` : ''}${formula} - ${theme}${options ? ` - ${options}` : ''} ${startDate}-${endDate}`
  }

  /**
   * get enrollment main trip details
   * @param enrollment
   * @returns string representing the main trip details of the enrollment
   */
  static getEnrollmentMainTripDetails(enrollment: EnrollmentMainTripDetailsInput): string {
    const mainTrip = enrollment.mainProposition?.mainTrip
    return TripProperties.getTripLabel(mainTrip)
  }

  /**
   * get enrollment secondary trip details
   * @param enrollment
   * @returns string representing the main trip details of the enrollment
   */
  static getEnrollmentSecondaryTripDetails(
    enrollment: EnrollmentSecondaryTripDetailsInput
  ): string {
    const secondaryTrip = enrollment.mainProposition?.secondaryTrip
    return TripProperties.getTripLabel(secondaryTrip)
  }

  /**
   * get the accomodation category of the enrollment
   * @param enrollment
   * @returns the accomodation category of the enrollment
   */
  static getEnrollmentAccomodation(
    enrollment: GetEnrollmentAccomodationInput
  ): AccomodationEnum | undefined {
    return enrollment.enrollmentCenter?.center.accomodation
  }

  /**
   * check if the enrollment is a host family
   * @param enrollment
   * @returns true if the enrollment is a host family
   */
  static isHostFamily(enrollment: GetEnrollmentAccomodationInput): boolean {
    return this.getEnrollmentAccomodation(enrollment) === AccomodationEnum.HOST_FAMILY
  }

  static hasCompatibleCenterAccomodation(
    enrollment: HasCompatibleCenterAccomodationInput,
    center: CenterAccomodationInput
  ): boolean {
    const category = enrollment.mainProposition?.journey?.date.product.accomodationCategory
    if (!category) return false

    return (
      (category === AccomodationCategoryEnum.HOST_FAMILY) ===
      (center.accomodation === AccomodationEnum.HOST_FAMILY)
    )
  }

  static getHostFamily(enrollment: GetHostFamilyInput): HostFamilyLight | undefined {
    return enrollment.enrollmentCenter?.hostFamily
  }

  static hasTransport(enrollment?: EnrollmentBaseType): boolean {
    return !!enrollment?.mainProposition?.mainTrip?.hasTransport
  }

  static isCancelled<T extends { status: ApplicationStatusEnum }>(enrollment: T): boolean {
    return (
      enrollment.status === ApplicationStatusEnum.CANCELLED_AFTER_CONFIRMATION ||
      enrollment.status === ApplicationStatusEnum.CANCELLED_BEFORE_CONFIRMATION
    )
  }

  // only a confirmed enrollment can be affected to a center (the server refuses the others)
  static isAffectable<T extends { status: ApplicationStatusEnum }>(enrollment: T): boolean {
    return enrollment.status === ApplicationStatusEnum.CONFIRMED
  }

  static isConfirmed<T extends { status: ApplicationStatusEnum; confirmedAt?: Date | null }>(
    enrollment: T
  ): boolean {
    return !!enrollment.confirmedAt || enrollment.status === ApplicationStatusEnum.CONFIRMED
  }

  static declaredFriendsChanged<T extends FriendInput>(
    oldDeclaredFriends: T[],
    newDeclaredFriends: T[]
  ): boolean {
    // Check all the fields of the declared friends (firstName, lastName, type) to determine if there is a change
    const constructKey = (friend: T) => `${friend?.firstName}-${friend?.lastName}-${friend?.type}`
    const oldFriends = oldDeclaredFriends?.map(constructKey).sort().join(',')
    const newFriends = newDeclaredFriends?.map(constructKey).sort().join(',')
    return oldFriends !== newFriends
  }
}
